import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AgentBusyError,
  AgentQuotaError,
  createAgentDialog,
  editAgentMessage,
  listAgentMessages,
  replyAgentMessage,
  streamAgentMessage,
  type AgentDialogMessage,
  type AgentPageContext,
} from '@/modules/personal-agent-api';

// `createdAt` is the wire timestamp for hydrated rows and the local clock
// for rows this hook appended itself — good enough for the relative
// timestamps the thread shows, never used for ordering decisions beyond
// the initial hydration sort. `id` is the server's message id — what a
// journal note links to and what edit() addresses; null on rows this hook
// appended until the post-answer transcript re-read pairs them (the stream
// never carries it).
export type AgentChatMessage = {
  id: string | null;
  role: 'user' | 'assistant';
  text: string;
  createdAt: string;
};

const TRANSCRIPT_LIMIT = 100;

// Wire order is unspecified — the thread reads oldest-first.
function sortRows(rows: AgentDialogMessage[]): AgentDialogMessage[] {
  // Stryker disable next-line MethodExpression: equivalent — the defensive copy protects an array that is call-local anyway; sorting it in place is unobservable
  return rows.slice().sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

function toChatMessages(rows: AgentDialogMessage[]): AgentChatMessage[] {
  return sortRows(rows).map((row) => ({
    id: row.id,
    role: row.role,
    text: row.text,
    createdAt: row.createdAt,
  }));
}

// Pairs the server transcript onto the local rows from the end, while role
// and text agree — the one alignment that survives a stop() (the local
// thread then lacks the answer the server holds) without attaching an id
// to the wrong row. Unpaired rows keep a null id; the next hydration
// carries the server's. Exported for the tests alone; not on the barrel.
export function attachIds(
  local: AgentChatMessage[],
  rows: AgentDialogMessage[],
): AgentChatMessage[] {
  const sorted = sortRows(rows);
  const next = local.slice();
  for (let i = 1; ; i++) {
    const mine = local.at(-i);
    const theirs = sorted.at(-i);
    if (mine === undefined || theirs === undefined) break;
    if (mine.role !== theirs.role || mine.text !== theirs.text) break;
    next[local.length - i] = { ...mine, id: theirs.id };
  }
  return next;
}

// The local mirror of the backend's edit: the row with `messageId` keeps
// its id under the new text and every row after it goes. An id the thread
// does not hold leaves the rows as they are. Exported for the tests alone;
// not on the barrel.
export function applyEdit(
  local: AgentChatMessage[],
  messageId: string,
  text: string,
): AgentChatMessage[] {
  const next: AgentChatMessage[] = [];
  for (const row of local) {
    if (row.id === messageId) {
      next.push({ ...row, text });
      return next;
    }
    next.push(row);
  }
  return next;
}

async function readTranscript(
  dialogId: string,
): Promise<AgentDialogMessage[] | null> {
  try {
    return await listAgentMessages(dialogId, TRANSCRIPT_LIMIT);
  } catch {
    return null;
  }
}

// What the thread shows when a run fails. `send`: the question never
// reached the backend — it is rolled back out of the thread and `question`
// carries it for the retry affordance (a re-send). `reply`: the dialog's
// last message is stored but its answer failed (a send the backend accepted
// before failing, a reply, an edit whose answer failed) — the question
// stays and the retry is reply(); a re-send would store it twice. `edit`:
// the rewrite itself failed — the thread is unchanged and the recovery is
// opening Edit again. `busy` = the dialog is still answering a previous
// message server-side (one answer per dialog at a time — possible after
// stop(), which abandons the stream locally while the server keeps
// generating).
export type AgentChatFailure =
  | {
      kind: 'send';
      reason: 'busy' | 'rate_limit' | 'error';
      question: string;
      retryAfterSeconds: number | null;
    }
  | {
      kind: 'reply' | 'edit';
      reason: 'busy' | 'rate_limit' | 'error';
      retryAfterSeconds: number | null;
    }
  | { kind: 'load' };

// Terminal outcome of one answering run — a submitted question, a reply or
// an edit — exactly one per run the user actually saw end (superseded runs
// report nothing). `tokens` is the stream's done-frame count, null for
// every non-done outcome.
export type AgentFinish = {
  status: 'done' | 'stopped' | 'busy' | 'rate_limited' | 'error';
  tokens: number | null;
};

// One answering run's bookkeeping. `step` is what the thread owes a rollback
// for: `send` until the backend accepts the message (the optimistic row is
// local-only and comes back out on failure or stop), `edit` while the
// rewrite is in flight (nothing on screen has changed yet), `reply` once
// the question is stored server-side (a failure drops only this run's
// partial answer; the question stays for reply()). `answered` = this run
// has put an assistant row on screen.
type Run = {
  step:
    | { kind: 'send'; question: AgentChatMessage }
    | { kind: 'edit' }
    | { kind: 'reply' };
  answered: boolean;
};

// Drives one personal-agent conversation as a full visible thread (unlike
// the legacy one-question-one-answer assistant, history stays on screen).
// The dialog is created lazily on the first submit and kept for follow-ups;
// openDialog hydrates an existing transcript (answers survive connection
// drops server-side, so a reopened dialog shows the full answer a dropped
// stream lost). Auth is the app session's — the API module rides the SanR
// fetch pipeline, so an expired or revoked session is refreshed or torn
// down before any outcome reaches this hook; what does reach it is a plain
// failure.
//
// Page context rides per submit; the heavy fields are sent once per
// dialog+url (the backend caps context at 64 KiB per message — re-sending
// an unchanged page every turn would burn the budget) and again when the
// url changes; follow-ups carry url/title alone.
export function useAgentDialog(opts: {
  onFinish?: (finish: AgentFinish) => void;
}): {
  messages: AgentChatMessage[];
  // Non-null disables the input: a transcript hydration or a streaming
  // answer is in flight.
  busy: 'loading' | 'streaming' | null;
  failure: AgentChatFailure | null;
  submit: (text: string, pageContext?: AgentPageContext) => void;
  // Rewrites a sent message (by its server id) and answers it anew — the
  // backend deletes every later message, so the thread truncates after it.
  edit: (messageId: string, text: string) => void;
  // Answers the message the dialog ends with — the retry for a `reply`
  // failure.
  reply: () => void;
  stop: () => void;
  openDialog: (id: string) => void;
  retryLoad: () => void;
} {
  const [messages, setMessages] = useState<AgentChatMessage[]>([]);
  const [busy, setBusy] = useState<'loading' | 'streaming' | null>(null);
  const [failure, setFailure] = useState<AgentChatFailure | null>(null);

  // Run identity is object identity, not a counter — a stale run can never
  // be mistaken for the live one. Answering runs and hydrations supersede
  // each other; cancelRun retires both.
  const runRef = useRef<Run | null>(null);
  const loadRef = useRef<object | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const dialogRef = useRef<string | null>(null);
  // The url whose heavy context already went out in the CURRENT dialog —
  // openDialog resets it, and a fresh dialog starts with it null, so no
  // dialog id needs to ride along.
  const heavySentRef = useRef<string | null>(null);
  const lastOpenedRef = useRef<string | null>(null);
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  });

  // eslint-disable-next-line no-restricted-syntax -- the compiler skips this hook (a ??= assignment), so this memo still carries its render-skipping
  const cancelRun = useCallback(
    (): void => {
      runRef.current = null;
      loadRef.current = null;
      abortRef.current?.abort();
    },
    // Stryker disable next-line ArrayDeclaration: a mutated constant deps array is Object.is-equal on every render, so the callback stays stable either way
    [],
  );

  useEffect(
    () => cancelRun,
    // Stryker disable next-line ArrayDeclaration: a mutated constant deps array is Object.is-equal on every render, so the effect stays mount-only either way
    [cancelRun],
  );

  const startRun = (step: Run['step']): { run: Run; signal: AbortSignal } => {
    cancelRun();
    const run: Run = { step, answered: false };
    runRef.current = run;
    const controller = new AbortController();
    abortRef.current = controller;
    setFailure(null);
    setBusy('streaming');
    return { run, signal: controller.signal };
  };

  // The run's answer streams into one assistant row at the tail: the first
  // chunk appends it, later chunks replace it. `first` is fixed before the
  // updater runs, so a double-invoked updater stays correct.
  const onAnswerFor = (run: Run) => {
    const createdAt = new Date().toISOString();
    return (answer: string): void => {
      if (runRef.current !== run) return;
      const first = !run.answered;
      run.answered = true;
      setMessages((prev) => [
        ...(first ? prev : prev.slice(0, -1)),
        { id: null, role: 'assistant', text: answer, createdAt },
      ]);
    };
  };

  // Ids exist only on the transcript (the stream never carries the user
  // message's, and the journal Save needs it): one re-read per answer. A
  // failed re-read leaves the turn id-less — the thread still reads right,
  // Save just stays unavailable for those rows until the next hydration.
  const settle = async (
    run: Run,
    dialogId: string,
    tokens: number | null,
  ): Promise<void> => {
    const rows = await readTranscript(dialogId);
    if (runRef.current !== run) return;
    if (rows !== null) setMessages((prev) => attachIds(prev, rows));
    setBusy(null);
    optsRef.current.onFinish?.({ status: 'done', tokens });
  };

  // A live run's rejection: the thread gives back what this run alone put
  // on it, the failure block names the recovery. An answered run is always
  // past the `reply` step (chunks arrive only after acceptance, and an edit
  // streams only after its rewrite landed), so its partial is the tail.
  const fail = (run: Run, err: unknown): void => {
    const { step } = run;
    if (step.kind === 'send') {
      const { question } = step;
      setMessages((prev) => prev.filter((m) => m !== question));
    } else if (run.answered) {
      setMessages((prev) => prev.slice(0, -1));
    }
    setBusy(null);
    const reason =
      err instanceof AgentBusyError ? 'busy'
      : err instanceof AgentQuotaError ? 'rate_limit'
      : 'error';
    const retryAfterSeconds =
      err instanceof AgentQuotaError ? err.retryAfterSeconds : null;
    setFailure(
      step.kind === 'send' ?
        {
          kind: 'send',
          reason,
          question: step.question.text,
          retryAfterSeconds,
        }
      : { kind: step.kind, reason, retryAfterSeconds },
    );
    optsRef.current.onFinish?.({
      status:
        reason === 'busy' ? 'busy'
        : reason === 'rate_limit' ? 'rate_limited'
        : 'error',
      tokens: null,
    });
  };

  const submit = (text: string, pageContext?: AgentPageContext): void => {
    const question = text.trim();
    if (question === '' || busy !== null) return;
    const userMessage: AgentChatMessage = {
      id: null,
      role: 'user',
      text: question,
      createdAt: new Date().toISOString(),
    };
    const { run, signal } = startRun({ kind: 'send', question: userMessage });
    setMessages((prev) => [...prev, userMessage]);
    void (async () => {
      try {
        dialogRef.current ??= await createAgentDialog();
        if (runRef.current !== run) return;
        const dialogId = dialogRef.current;

        const heavyAlreadySent = heavySentRef.current === pageContext?.url;
        const context =
          // Stryker disable next-line ConditionalExpression: equivalent — with no pageContext the heavy branch also resolves to undefined (heavyAlreadySent is false and pageContext passes through)
          pageContext === undefined ? undefined
          : heavyAlreadySent ?
            { url: pageContext.url, title: pageContext.title }
          : pageContext;
        if (pageContext !== undefined && !heavyAlreadySent)
          heavySentRef.current = pageContext.url;

        const { tokens } = await streamAgentMessage(
          dialogId,
          {
            text: question,
            ...(context !== undefined && { pageContext: context }),
          },
          onAnswerFor(run),
          signal,
          () => {
            run.step = { kind: 'reply' };
          },
        );
        if (runRef.current !== run) return;
        await settle(run, dialogId, tokens);
      } catch (err) {
        if (runRef.current !== run) return;
        fail(run, err);
      }
    })();
  };

  const reply = (): void => {
    const dialogId = dialogRef.current;
    if (busy !== null || dialogId === null) return;
    const { run, signal } = startRun({ kind: 'reply' });
    void (async () => {
      try {
        const { tokens } = await replyAgentMessage(
          dialogId,
          onAnswerFor(run),
          signal,
        );
        if (runRef.current !== run) return;
        await settle(run, dialogId, tokens);
      } catch (err) {
        if (runRef.current !== run) return;
        fail(run, err);
      }
    })();
  };

  const edit = (messageId: string, text: string): void => {
    const question = text.trim();
    const dialogId = dialogRef.current;
    if (question === '' || busy !== null || dialogId === null) return;
    const { run, signal } = startRun({ kind: 'edit' });
    void (async () => {
      try {
        await editAgentMessage(dialogId, messageId, question);
        if (runRef.current !== run) return;
        run.step = { kind: 'reply' };
        setMessages((prev) => applyEdit(prev, messageId, question));
        const { tokens } = await replyAgentMessage(
          dialogId,
          onAnswerFor(run),
          signal,
        );
        if (runRef.current !== run) return;
        await settle(run, dialogId, tokens);
      } catch (err) {
        if (runRef.current !== run) return;
        fail(run, err);
      }
    })();
  };

  const stop = (): void => {
    if (busy !== 'streaming') return;
    const run = runRef.current;
    // Stryker disable next-line ConditionalExpression: equivalent — busy === 'streaming' means an answering run is live, so runRef holds it
    if (run === null) return;
    // Nothing is generating while an edit's rewrite is in flight — it lands
    // or fails on its own; the answer that follows is what stop() cuts.
    if (run.step.kind === 'edit') return;
    cancelRun();
    // The streamed partial stays in the thread; the server finishes the
    // answer regardless and stores it, so a later hydration shows the
    // complete text. A question the backend has not accepted is local-only
    // and comes back out — it is the tail, nothing streams before
    // acceptance; a stored one stays.
    if (run.step.kind === 'send') setMessages((prev) => prev.slice(0, -1));
    setBusy(null);
    optsRef.current.onFinish?.({ status: 'stopped', tokens: null });
  };

  const openDialog = (id: string): void => {
    cancelRun();
    lastOpenedRef.current = id;
    dialogRef.current = id;
    heavySentRef.current = null;
    setFailure(null);
    setMessages([]);
    setBusy('loading');
    const load = {};
    loadRef.current = load;
    void (async () => {
      try {
        const rows = await listAgentMessages(id, TRANSCRIPT_LIMIT);
        if (loadRef.current !== load) return;
        setMessages(toChatMessages(rows));
        setBusy(null);
      } catch {
        if (loadRef.current !== load) return;
        setBusy(null);
        setFailure({ kind: 'load' });
      }
    })();
  };

  const retryLoad = (): void => {
    if (lastOpenedRef.current !== null) openDialog(lastOpenedRef.current);
  };

  return {
    messages,
    busy,
    failure,
    submit,
    edit,
    reply,
    stop,
    openDialog,
    retryLoad,
  };
}
